import { Input } from '../input'
import { GripVertical, Minus, Plus } from 'lucide-react'
import { closestCenter, DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import React from 'react'
import { MiniButton } from './mini-button'
import uniqueId from 'lodash.uniqueid'
import { shallow as shallowEquals } from 'zustand/shallow'
import { mergeClasses } from '../../../helpers/styling'

// Need id for stable sorting/react behaviour
type ItemWithId = { id: string; value: string }

export function TextArrayInput(props: {
  id?: string
  name?: string
  value: string[] | undefined
  onChange: (value: string[]) => void
  className?: string
  variant?: 'top-add-button' | 'bottom-add-button'
}) {
  const [ items, setItems ] = React.useState<ItemWithId[]>(() => itemsWithId(props.value))
  const divRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    const isSameValues = shallowEquals(props.value || [], items.map(i => i.value))
    if (isSameValues) return
    setItems(itemsWithId(props.value))
  }, [ props.value ])

  const setAndPublishItems = (newItems: ItemWithId[]) => {
    setItems(newItems)
    props.onChange(newItems.map(it => it.value))
  }

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const focusInput = (index: number) => {
    requestAnimationFrame(() => {
      divRef.current
        ?.querySelector<HTMLInputElement>(`input[data-index="${index}"]`)
        ?.focus()
    })
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={e => {
        if (!e.over) return
        const oldIndex = items.findIndex(i => i.id === e.active.id)
        const newIndex = items.findIndex(i => i.id === e.over!.id)
        if (oldIndex !== newIndex) {
          setAndPublishItems(arrayMove(items, oldIndex, newIndex))
          focusInput(newIndex)
        }
      }}
    >
      <SortableContext
        items={items.map(i => i.id)}
        strategy={verticalListSortingStrategy}
      >
        <div id={props.id}
             role="group"
             ref={divRef}
             className={mergeClasses("flex flex-col gap-1", props.className)}>
            {(props.variant == null || props.variant === 'top-add-button') && (
              <MiniButton
                title="Add"
                className="w-full justify-center"
                onClick={() => {
                  const next = [ { id: uniqueId(), value: '' }, ...items ]
                  setAndPublishItems(next)
                  focusInput(next.length - 1)
                }}
              >
                <Plus size={11} />
              </MiniButton>
            )}
          {items.map((item, index) => (
            <SortableRow
              key={item.id}
              item={item}
              index={index}
              onChange={(i, v) => {
                const next = [...items]
                next[i] = { ...next[i], value: v }
                setAndPublishItems(next)
              }}
              onAddBelow={i => {
                const next = [...items]
                next.splice(i + 1, 0, { id: uniqueId(), value: '' })
                setAndPublishItems(next)
                focusInput(i + 1)
              }}
              onRemove={i => {
                const next = items.filter((_, idx) => idx !== i)
                setAndPublishItems(next)
                focusInput(Math.max(0, i - 1))
              }}
              focusInput={focusInput}
            />
          ))}
          {(props.variant === 'bottom-add-button') && (
            <MiniButton
              title="Add"
              className="w-full justify-center"
              onClick={() => {
                const next = [ ...items, { id: uniqueId(), value: '' } ]
                setAndPublishItems(next)
                focusInput(next.length - 1)
              }}
            >
              <Plus size={11} />
            </MiniButton>
          )}
        </div>
      </SortableContext>
    </DndContext>
  )
}

function SortableRow(props: {
  item: ItemWithId
  index: number
  onChange: (i: number, v: string) => void
  onAddBelow: (i: number) => void
  onRemove: (i: number) => void
  focusInput: (i: number) => void
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: props.item.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <div ref={setNodeRef} style={style} className="flex items-center gap-1">
      <button
        type="button"
        className="inline-flex flex-grow items-center opacity-50 cursor-move hover:opacity-80"
        {...attributes}
        {...listeners}
        tabIndex={-1}
      >
        <GripVertical size={11} />
      </button>

      <Input
        data-index={props.index}
        className="
          text-xs !px-[12px] h-7 rounded-sm
          focus:ring-2 focus:ring-blue-500
          focus-visible:ring-2 focus-visible:ring-blue-500
        "
        value={props.item.value}
        onChange={e => props.onChange(props.index, e.target.value)}
        onKeyDown={e => {
          const { selectionStart, selectionEnd, value } = e.currentTarget
          const atStart = selectionStart === 0 && selectionEnd === 0
          const atEnd =
            selectionStart === value.length &&
            selectionEnd === value.length

          if (e.key === 'Enter') {
            e.preventDefault()
            props.onAddBelow(props.index)
          }

          if (e.key === 'Backspace' && atStart) {
            e.preventDefault()
            props.onRemove(props.index)
          }

          if (e.key === 'Delete' && atEnd) {
            e.preventDefault()
            props.onRemove(props.index + 1)
          }

          if (e.key === 'ArrowUp') {
            e.preventDefault()
            props.focusInput(props.index - 1)
          }

          if (e.key === 'ArrowDown') {
            e.preventDefault()
            props.focusInput(props.index + 1)
          }
        }}
      />

      <MiniButton title="Remove"
                  tabIndex={-1}
                  onClick={() => props.onRemove(props.index)}>
        <Minus size={11} />
      </MiniButton>
      <MiniButton title="Add below"
                  tabIndex={-1}
                  onClick={() => props.onAddBelow(props.index)}>
        <Plus size={11} />
      </MiniButton>
    </div>
  )
}

function itemsWithId(value?: string[]): ItemWithId[] {
  return (value ?? []).map(v => ({ id: uniqueId(), value: v }))
}
